import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?auto=format&fit=crop&w=1400&q=70";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Selamat datang kembali!");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-950/60 via-blue-900/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs uppercase tracking-[0.2em]">
            <TrendingUp className="w-3.5 h-3.5" /> Pelacak Aset Bulanan
          </div>
          <h2 className="mt-6 text-4xl font-display font-semibold leading-tight tracking-tight">
            Pantau net worth Anda dari bulan ke bulan.
          </h2>
          <p className="mt-3 text-white/80 max-w-md">
            Catat saldo, lihat pertumbuhan, dan pahami komposisi kekayaan Anda dalam satu dashboard yang bersih.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8 flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center font-display font-bold">
              A
            </div>
            <div>
              <div className="font-display font-semibold text-slate-900">AsetKu</div>
              <div className="text-xs text-slate-500 -mt-0.5">Monthly Asset Tracker</div>
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Masuk ke akun Anda
          </h1>
          <p className="mt-2 text-slate-600">Lanjutkan mencatat aset bulanan Anda.</p>

          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] text-slate-500 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="anda@email.com"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.1em] text-slate-500 font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
                className="h-11"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-6 w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-md font-medium"
          >
            {busy ? "Memproses…" : (<span className="inline-flex items-center gap-2">Masuk <ArrowRight className="w-4 h-4" /></span>)}
          </Button>

          <p className="mt-6 text-sm text-slate-600">
            Belum punya akun?{" "}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium" data-testid="goto-register-link">
              Daftar sekarang
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
